import type { ReactNode } from "react";

import {
  type ISelectionPayload,
  SelectedDataType,
  type SelectedData,
} from "../map-panel-context";

import { BoundaryPanelRouter } from "./components/boundary-panel-router";
import ProjectClusterPanelContent from "./components/project-cluster-panel-content";
import { ProjectClusterPanelSkeleton } from "./components/project-cluster-panel-skeleton";
import ProjectPanelContent from "./components/project-panel-content";
import { ProjectPanelSkeleton } from "./components/project-panel-skeleton";
import SchoolClusterPanelContent from "./components/school-cluster-panel-content";
import { SchoolClusterPanelSkeleton } from "./components/school-cluster-panel-skeleton";
import SchoolPanelContent from "./components/school-panel-content";
import { SchoolPanelSkeleton } from "./components/school-panel-skeleton";
import { SearchMarkerPanelContent } from "./components/search-marker-panel-content";
import SelectionPanelContent from "./components/selection-panel-content";
import StudentClusterPanelContent from "./components/student-cluster-panel-content";
import { StudentClusterPanelSkeleton } from "./components/student-cluster-panel-skeleton";
import StudentPanelContent from "./components/student-panel-content";
import { StudentPanelSkeleton } from "./components/student-panel-skeleton";
import ZonesPanelContent from "./components/zones-selection/zones-panel-content";
import { ZonesPanelSkeleton } from "./components/zones-selection/zones-panel-skeleton";

import type { BoundarySchoolSelectVariant } from "./components/boundary-panel-content";

function noopSchoolClusterSelect(_schoolId: string): void {
  return;
}

function noopProjectClusterSelect(_projectId: string): void {
  return;
}

function noopZoneSelect(_previewId: string, _documentId: string): void {
  return;
}

export interface RenderDetailsPanelContentOptions {
  selectedData: SelectedData | null;
  selectionData: ISelectionPayload | null;
  isStudentLoading: boolean;
  isSchoolLoading: boolean;
  isSelectionLoading: boolean;
  isBoundaryLoading: boolean;
  isStudentClusterLoading: boolean;
  isSchoolClusterLoading: boolean;
  isProjectLoading: boolean;
  isProjectClusterLoading: boolean;
  onSchoolClusterSelect?: (schoolId: string) => void;
  onProjectClusterSelect?: (projectId: string) => void;
  onZoneSelect?: (previewId: string, documentId: string) => void;
  isZonesLoading?: boolean;
  /** Map-level zones omit Summary skeleton cards. Default true. */
  zonesShowSummary?: boolean;
  boundarySchoolSelectVariant?: BoundarySchoolSelectVariant;
}

function renderStudentPanel(
  data: Extract<SelectedData, { type: SelectedDataType.STUDENT }>,
  isLoading: boolean,
): ReactNode {
  if (isLoading) return <StudentPanelSkeleton />;
  return <StudentPanelContent payload={data.payload} />;
}

function renderSchoolPanel(
  data: Extract<SelectedData, { type: SelectedDataType.SCHOOL }>,
  isLoading: boolean,
): ReactNode {
  if (isLoading) return <SchoolPanelSkeleton />;
  return (
    <SchoolPanelContent
      payload={data.payload}
    />
  );
}

function renderSelectionPanel(
  data: Extract<SelectedData, { type: SelectedDataType.SELECTION }>,
  isCountsLoading: boolean,
): ReactNode {
  return (
    <SelectionPanelContent
      isCountsLoading={isCountsLoading}
      payload={data.payload}
    />
  );
}

function renderBoundaryPanel(
  data: Extract<SelectedData, { type: SelectedDataType.BOUNDARY }>,
  isLoading: boolean,
  schoolSelectVariant?: BoundarySchoolSelectVariant,
): ReactNode {
  return (
    <BoundaryPanelRouter
      isLoading={isLoading}
      payload={data.payload}
      schoolSelectVariant={schoolSelectVariant}
    />
  );
}

function renderStudentClusterPanel(
  data: Extract<SelectedData, { type: SelectedDataType.STUDENT_CLUSTER }>,
  isLoading: boolean,
): ReactNode {
  if (isLoading) return <StudentClusterPanelSkeleton />;
  return <StudentClusterPanelContent payload={data.payload} />;
}

function renderProjectPanel(
  data: Extract<SelectedData, { type: SelectedDataType.PROJECT }>,
  isLoading: boolean,
): ReactNode {
  if (isLoading) return <ProjectPanelSkeleton />;
  return <ProjectPanelContent payload={data.payload} />;
}

function renderProjectClusterPanel(
  data: Extract<SelectedData, { type: SelectedDataType.PROJECT_CLUSTER }>,
  isLoading: boolean,
  onProjectClusterSelect: ((projectId: string) => void) | undefined,
): ReactNode {
  if (isLoading) return <ProjectClusterPanelSkeleton />;
  return (
    <ProjectClusterPanelContent
      payload={data.payload}
      onProjectSelect={onProjectClusterSelect ?? noopProjectClusterSelect}
    />
  );
}

function renderZonesPanel(
  data: Extract<SelectedData, { type: SelectedDataType.ZONES }>,
  isLoading: boolean,
  onZoneSelect: ((previewId: string, documentId: string) => void) | undefined,
  showSummary = true,
): ReactNode {
  if (isLoading) return <ZonesPanelSkeleton showSummary={showSummary} />;
  return (
    <ZonesPanelContent
      payload={data.payload}
      onZoneSelect={onZoneSelect ?? noopZoneSelect}
    />
  );
}

function renderSchoolClusterPanel(
  data: Extract<SelectedData, { type: SelectedDataType.SCHOOL_CLUSTER }>,
  isLoading: boolean,
  onSchoolClusterSelect: ((schoolId: string) => void) | undefined,
): ReactNode {
  if (isLoading) return <SchoolClusterPanelSkeleton />;
  return (
    <SchoolClusterPanelContent
      payload={data.payload}
      onSchoolSelect={onSchoolClusterSelect ?? noopSchoolClusterSelect}
    />
  );
}

function renderSearchMarkerPanel(
  data: Extract<SelectedData, { type: SelectedDataType.SEARCH_MARKER }>,
): ReactNode {
  return <SearchMarkerPanelContent payload={data.payload} />;
}

const PANEL_RENDERERS: Record<
  SelectedDataType,
  (options: RenderDetailsPanelContentOptions) => ReactNode
> = {
  [SelectedDataType.STUDENT]: (options) =>
    renderStudentPanel(
      options.selectedData as Extract<SelectedData, { type: SelectedDataType.STUDENT }>,
      options.isStudentLoading,
    ),
  [SelectedDataType.SCHOOL]: (options) =>
    renderSchoolPanel(
      options.selectedData as Extract<SelectedData, { type: SelectedDataType.SCHOOL }>,
      options.isSchoolLoading,
    ),
  [SelectedDataType.SELECTION]: (options) =>
    renderSelectionPanel(
      options.selectedData as Extract<SelectedData, { type: SelectedDataType.SELECTION }>,
      options.isSelectionLoading,
    ),
  [SelectedDataType.BOUNDARY]: (options) =>
    renderBoundaryPanel(
      options.selectedData as Extract<SelectedData, { type: SelectedDataType.BOUNDARY }>,
      options.isBoundaryLoading,
      options.boundarySchoolSelectVariant,
    ),
  [SelectedDataType.STUDENT_CLUSTER]: (options) =>
    renderStudentClusterPanel(
      options.selectedData as Extract<SelectedData, { type: SelectedDataType.STUDENT_CLUSTER }>,
      options.isStudentClusterLoading,
    ),
  [SelectedDataType.SCHOOL_CLUSTER]: (options) =>
    renderSchoolClusterPanel(
      options.selectedData as Extract<SelectedData, { type: SelectedDataType.SCHOOL_CLUSTER }>,
      options.isSchoolClusterLoading,
      options.onSchoolClusterSelect,
    ),
  [SelectedDataType.PROJECT]: (options) =>
    renderProjectPanel(
      options.selectedData as Extract<SelectedData, { type: SelectedDataType.PROJECT }>,
      options.isProjectLoading,
    ),
  [SelectedDataType.PROJECT_CLUSTER]: (options) =>
    renderProjectClusterPanel(
      options.selectedData as Extract<SelectedData, { type: SelectedDataType.PROJECT_CLUSTER }>,
      options.isProjectClusterLoading,
      options.onProjectClusterSelect,
    ),
  [SelectedDataType.ZONES]: (options) =>
    renderZonesPanel(
      options.selectedData as Extract<SelectedData, { type: SelectedDataType.ZONES }>,
      options.isZonesLoading ?? false,
      options.onZoneSelect,
      options.zonesShowSummary ?? true,
    ),
  [SelectedDataType.SEARCH_MARKER]: (options) =>
    renderSearchMarkerPanel(
      options.selectedData as Extract<SelectedData, { type: SelectedDataType.SEARCH_MARKER }>,
    ),
};

export function renderDetailsPanelContent(
  options: RenderDetailsPanelContentOptions,
): ReactNode {
  const { selectedData } = options;
  if (!selectedData) return null;

  return PANEL_RENDERERS[selectedData.type]?.(options) ?? null;
}
